import { Alert, Button, Checkbox, Descriptions, Space, Tag, Typography } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    adjustRect,
    buildCalibrationCandidate,
    buildManualCandidate,
    describeCalibrationGeometry,
    rectFromCorners,
    type CalibrationCandidate,
    type CalibrationSource,
    type CaptureCalibration,
    type NormalizedRect,
} from '../domain/opticalCalibration';

/**
 * Lets an operator say which part of their card carries the domicile block.
 *
 * The service measured where ink is and stopped there, because working out *what* a band says would
 * mean recognising text on an identity document purely to place a rectangle. So the identification
 * is done by the one party who can already read the card without anything being recorded: the person
 * holding it.
 *
 * **Two illuminations, and no preference between them.** Both frames are measured and both are
 * shown, because which one this card segments on is the open question of item 13 - the legacy system
 * read its address off the infrared frame, which is evidence about its authors and not proof about
 * this reader. Nothing here is preselected and nothing is ranked.
 *
 * **A rectangle can always be drawn.** On 2026-09-14 automatic segmentation offered one block
 * covering the whole card, the operator refused it correctly, and the physical scan ended with
 * nothing recorded because there was no other route. There is now.
 *
 * Nothing in this panel leaves the browser and nothing is persisted. The previews are the existing
 * session-scoped object URLs, the overlay is drawn locally over them, and the only thing the
 * operator can take away is geometry.
 */
export interface DocumentCalibrationPanelProps {
    /** Every source the scan measured, in a fixed order and unranked. */
    calibrations: readonly CaptureCalibration[];
    /** The session-scoped object URL of each capture, or null where there is none. */
    previews: { readonly visible: string | null; readonly infrared: string | null };
}

/** How far one arrow-key press moves or resizes the rectangle. */
const KEYBOARD_STEP = 0.005;

export const DocumentCalibrationPanel = ({
    calibrations,
    previews,
}: DocumentCalibrationPanelProps) => {
    const { t } = useTranslation();

    const [source, setSource] = useState<CalibrationSource | null>(null);
    const [selected, setSelected] = useState<readonly number[]>([]);
    const [manual, setManual] = useState<NormalizedRect | null>(null);
    const [drawing, setDrawing] = useState<{ x: number; y: number } | null>(null);
    const [mrzConfirmed, setMrzConfirmed] = useState(false);
    const [confirmed, setConfirmed] = useState(false);

    const surface = useRef<HTMLDivElement | null>(null);
    const rectangle = useRef<HTMLButtonElement | null>(null);

    // Nothing is preselected, including the source. A panel that opened on one illumination would be
    // making the comparison it exists to put to the operator.
    const active = calibrations.find(entry => entry.source === source) ?? null;

    const automatic = useMemo(
        () => buildCalibrationCandidate(active, selected),
        [active, selected],
    );

    const drawn = useMemo(() => buildManualCandidate(active, manual), [active, manual]);

    // The manual rectangle wins whenever one exists, because drawing one is a deliberate act that
    // follows looking at the bands and deciding they are not enough.
    const candidate: CalibrationCandidate | null = manual !== null ? drawn : automatic;

    const mrzUnknownBlocked =
        candidate !== null && candidate.safe && !candidate.mrzKnown && !mrzConfirmed;

    const confirmable = candidate !== null && candidate.safe && !mrzUnknownBlocked;

    const geometry = useMemo(
        () =>
            confirmed && active !== null && candidate !== null
                ? describeCalibrationGeometry(active, candidate)
                : null,
        [confirmed, active, candidate],
    );

    // Any change to a selection retracts a confirmation. A confirmed rectangle that silently
    // followed a later click would be a rectangle nobody actually approved.
    const retract = useCallback(() => {
        setConfirmed(false);
        setMrzConfirmed(false);
    }, []);

    const pickSource = (next: CalibrationSource) => {
        retract();
        setSource(next);
        setSelected([]);
        setManual(null);
    };

    const toggle = (index: number, next: boolean) => {
        retract();
        setManual(null);
        setSelected(current =>
            next ? [...current, index] : current.filter(entry => entry !== index),
        );
    };

    const pointAt = (event: React.PointerEvent<HTMLDivElement>) => {
        const box = surface.current?.getBoundingClientRect();

        if (!box || box.width <= 0 || box.height <= 0) {
            return null;
        }

        return {
            x: (event.clientX - box.left) / box.width,
            y: (event.clientY - box.top) / box.height,
        };
    };

    const startDraw = (event: React.PointerEvent<HTMLDivElement>) => {
        const point = pointAt(event);

        if (point === null) {
            return;
        }

        retract();
        setSelected([]);
        setDrawing(point);
        setManual(rectFromCorners(point, point));
    };

    const continueDraw = (event: React.PointerEvent<HTMLDivElement>) => {
        const point = pointAt(event);

        if (drawing === null || point === null) {
            return;
        }

        setManual(rectFromCorners(drawing, point));
    };

    // Once the pointer lets go, focus moves onto the rectangle it drew, so the arrow keys refine
    // it straight away. The rectangle is the thing being adjusted, so it is the thing that listens.
    const endDraw = () => {
        setDrawing(null);
        rectangle.current?.focus();
    };

    const nudge = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (manual === null) {
            return;
        }

        // Shift resizes, plain arrows move. Both are needed: an operator without a pointing device
        // still has to be able to place this rectangle, and it gates a physical calibration run.
        const resize = event.shiftKey;
        const step = KEYBOARD_STEP;

        const edit: Record<string, Parameters<typeof adjustRect>[1]> = {
            ArrowLeft: resize ? { dw: -step } : { dx: -step },
            ArrowRight: resize ? { dw: step } : { dx: step },
            ArrowUp: resize ? { dh: -step } : { dy: -step },
            ArrowDown: resize ? { dh: step } : { dy: step },
        };

        const change = edit[event.key];

        if (change === undefined) {
            return;
        }

        event.preventDefault();
        retract();
        setManual(current => (current === null ? null : adjustRect(current, change)));
    };

    return (
        <Space
            direction="vertical"
            size="middle"
            style={{ width: '100%' }}
            data-testid="calibration-panel"
        >
            <Alert
                type="info"
                showIcon
                message={t('documentScan.calibration.title')}
                description={t('documentScan.calibration.intro')}
            />

            <SourcePicker calibrations={calibrations} source={source} onPick={pickSource} />

            {active === null ? (
                <Alert
                    type="info"
                    showIcon
                    data-testid="calibration-no-source"
                    message={t('documentScan.calibration.pickSource')}
                    description={t('documentScan.calibration.pickSourceDetail')}
                />
            ) : (
                <>
                    <Alert
                        type="warning"
                        showIcon
                        data-testid="calibration-region-rules"
                        message={t('documentScan.calibration.regionRules')}
                        description={t('documentScan.calibration.regionRulesDetail')}
                    />

                    <Surface
                        calibration={active}
                        previewUrl={previews[active.source]}
                        selected={selected}
                        manual={manual}
                        candidate={candidate}
                        surface={surface}
                        rectangle={rectangle}
                        onPointerDown={startDraw}
                        onPointerMove={continueDraw}
                        onPointerUp={endDraw}
                        onKeyDown={nudge}
                    />

                    <BandPicker calibration={active} selected={selected} onToggle={toggle} />

                    <Space wrap>
                        <Button
                            data-testid="calibration-manual-clear"
                            disabled={manual === null}
                            onClick={() => {
                                retract();
                                setManual(null);
                            }}
                        >
                            {t('documentScan.calibration.clearRectangle')}
                        </Button>
                        <Button
                            data-testid="calibration-reject"
                            disabled={selected.length === 0}
                            onClick={() => {
                                retract();
                                setSelected([]);
                            }}
                        >
                            {t('documentScan.calibration.reject')}
                        </Button>
                    </Space>

                    <Problems candidate={candidate} />

                    {mrzUnknownBlocked && (
                        <Alert
                            type="warning"
                            showIcon
                            data-testid="calibration-mrz-unknown"
                            message={t('documentScan.calibration.mrzUnknown')}
                            description={
                                <Space direction="vertical">
                                    <span>{t('documentScan.calibration.mrzUnknownDetail')}</span>
                                    <Checkbox
                                        data-testid="calibration-mrz-confirm"
                                        checked={mrzConfirmed}
                                        onChange={event => setMrzConfirmed(event.target.checked)}
                                    >
                                        {t('documentScan.calibration.mrzUnknownConfirm')}
                                    </Checkbox>
                                </Space>
                            }
                        />
                    )}

                    <Button
                        type="primary"
                        disabled={!confirmable}
                        onClick={() => setConfirmed(true)}
                        data-testid="calibration-confirm"
                    >
                        {t('documentScan.calibration.confirm')}
                    </Button>

                    <Quality calibration={active} />
                </>
            )}

            {geometry !== null && (
                <>
                    <Alert
                        type="success"
                        showIcon
                        message={t('documentScan.calibration.confirmed')}
                        description={t('documentScan.calibration.confirmedDetail')}
                    />
                    <Typography.Paragraph
                        copyable={{ text: geometry }}
                        code
                        style={{ whiteSpace: 'pre-wrap', maxHeight: 240, overflow: 'auto' }}
                        data-testid="calibration-geometry"
                    >
                        {geometry}
                    </Typography.Paragraph>
                </>
            )}
        </Space>
    );
};

/**
 * Which illumination the operator is calibrating against.
 *
 * Never defaulted. The panel exists to put a comparison to a person, and opening on one of the two
 * would be the automatic selection the design forbids.
 */
const SourcePicker = ({
    calibrations,
    source,
    onPick,
}: {
    calibrations: readonly CaptureCalibration[];
    source: CalibrationSource | null;
    onPick: (next: CalibrationSource) => void;
}) => {
    const { t } = useTranslation();

    if (calibrations.length === 0) {
        return null;
    }

    return (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Typography.Text strong>{t('documentScan.calibration.source')}</Typography.Text>
            {/* Plain buttons rather than a radio group: this is a toggle between two views of the
                same scan, it must be reachable by keyboard and by assistive technology without a
                group label doing the explaining, and `aria-pressed` says which one is showing. */}
            <Space wrap>
                {calibrations.map(entry => (
                    <Button
                        key={entry.source}
                        data-testid={`calibration-source-${entry.source}`}
                        type={entry.source === source ? 'primary' : 'default'}
                        aria-pressed={entry.source === source}
                        onClick={() => onPick(entry.source)}
                    >
                        <Space size={6}>
                            <span>{t(`documentScan.calibration.sourceName.${entry.source}`)}</span>
                            <Tag color={entry.usable ? 'success' : 'default'}>
                                {t('documentScan.calibration.bandCount', {
                                    count: entry.bands.length,
                                })}
                            </Tag>
                        </Space>
                    </Button>
                ))}
            </Space>
        </Space>
    );
};

/** The preview with the measured bands, the zone and the operator's rectangle drawn over it. */
const Surface = ({
    calibration,
    previewUrl,
    selected,
    manual,
    candidate,
    surface,
    rectangle,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onKeyDown,
}: {
    calibration: CaptureCalibration;
    previewUrl: string | null;
    selected: readonly number[];
    manual: NormalizedRect | null;
    candidate: CalibrationCandidate | null;
    surface: React.MutableRefObject<HTMLDivElement | null>;
    rectangle: React.MutableRefObject<HTMLButtonElement | null>;
    onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
    onPointerUp: () => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
}) => {
    const { t } = useTranslation();

    if (previewUrl === null) {
        return (
            <Alert
                type="info"
                showIcon
                data-testid="calibration-no-preview"
                message={t('documentScan.calibration.noPreview')}
                description={t('documentScan.calibration.noPreviewDetail')}
            />
        );
    }

    // A drawing surface, not a control: the pointer draws here, and the keyboard works on the
    // rectangle it drew, which is the one focusable thing on the picture.
    return (
        <div
            ref={surface}
            data-testid="calibration-manual-surface"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            style={{
                position: 'relative',
                display: 'inline-block',
                maxWidth: '100%',
                touchAction: 'none',
                cursor: 'crosshair',
            }}
        >
            <img
                src={previewUrl}
                alt={t('documentScan.calibration.previewAlt')}
                draggable={false}
                style={{ display: 'block', maxWidth: '100%', maxHeight: 320 }}
            />

            {calibration.bands.map(band => {
                const isSelected = selected.includes(band.index);

                return (
                    <div
                        key={band.index}
                        data-testid={`calibration-band-${band.index}`}
                        style={{
                            position: 'absolute',
                            left: `${band.left * 100}%`,
                            top: `${band.top * 100}%`,
                            width: `${(band.right - band.left) * 100}%`,
                            height: `${(band.bottom - band.top) * 100}%`,
                            border: `2px solid ${isSelected ? '#52c41a' : '#1677ff'}`,
                            background: isSelected ? 'rgba(82,196,26,0.18)' : 'transparent',
                            pointerEvents: 'none',
                        }}
                    >
                        <span
                            style={{
                                position: 'absolute',
                                top: -2,
                                left: -2,
                                background: isSelected ? '#52c41a' : '#1677ff',
                                color: '#fff',
                                fontSize: 11,
                                padding: '0 4px',
                            }}
                        >
                            {band.index}
                        </span>
                    </div>
                );
            })}

            {/* The zone, drawn as a place to stay out of rather than as a place to pick. */}
            {calibration.mrzAvailable && (
                <div
                    data-testid="calibration-mrz-region"
                    style={{
                        position: 'absolute',
                        left: `${calibration.mrzLeft * 100}%`,
                        top: `${calibration.mrzTop * 100}%`,
                        width: `${(calibration.mrzRight - calibration.mrzLeft) * 100}%`,
                        height: `${(calibration.mrzBottom - calibration.mrzTop) * 100}%`,
                        border: '2px solid #cf1322',
                        background: 'rgba(207,19,34,0.12)',
                        pointerEvents: 'none',
                    }}
                />
            )}

            {/* The operator's rectangle is a button because it is the thing the arrow keys act on:
                focusable, labelled with its keys, and transparent to the pointer so a fresh drag
                across it starts a new rectangle instead of landing on the old one. */}
            {manual !== null && (
                <button
                    type="button"
                    ref={rectangle}
                    data-testid="calibration-manual-rect"
                    aria-label={t('documentScan.calibration.rectangleLabel')}
                    onKeyDown={onKeyDown}
                    style={{
                        position: 'absolute',
                        left: `${manual.x * 100}%`,
                        top: `${manual.y * 100}%`,
                        width: `${manual.w * 100}%`,
                        height: `${manual.h * 100}%`,
                        padding: 0,
                        border: `2px solid ${candidate?.safe ? '#fa8c16' : '#cf1322'}`,
                        background: 'rgba(250,140,22,0.12)',
                        pointerEvents: 'none',
                    }}
                />
            )}

            {manual === null && candidate?.safe && (
                <div
                    data-testid="calibration-candidate"
                    style={{
                        position: 'absolute',
                        left: `${candidate.rect.x * 100}%`,
                        top: `${candidate.rect.y * 100}%`,
                        width: `${candidate.rect.w * 100}%`,
                        height: `${candidate.rect.h * 100}%`,
                        border: '2px dashed #fa8c16',
                        pointerEvents: 'none',
                    }}
                />
            )}
        </div>
    );
};

/** The automatic bands, when segmentation found any that survived validation. */
const BandPicker = ({
    calibration,
    selected,
    onToggle,
}: {
    calibration: CaptureCalibration;
    selected: readonly number[];
    onToggle: (index: number, next: boolean) => void;
}) => {
    const { t } = useTranslation();

    if (calibration.bands.length === 0) {
        return (
            <Alert
                type="info"
                showIcon
                data-testid="calibration-no-bands"
                message={t('documentScan.calibration.noBands')}
                description={t('documentScan.calibration.noBandsDetail')}
            />
        );
    }

    return (
        <Space direction="vertical" size={4}>
            <Typography.Text strong>{t('documentScan.calibration.pick')}</Typography.Text>

            {calibration.bands.map(band => (
                <Checkbox
                    key={band.index}
                    data-testid={`calibration-band-check-${band.index}`}
                    checked={selected.includes(band.index)}
                    disabled={band.looksLikeCodeline}
                    onChange={event => onToggle(band.index, event.target.checked)}
                >
                    <Space size={6}>
                        <span>{t('documentScan.calibration.band', { index: band.index })}</span>
                        {band.looksLikeCodeline && (
                            <Tag color="error">{t('documentScan.calibration.codeline')}</Tag>
                        )}
                    </Space>
                </Checkbox>
            ))}
        </Space>
    );
};

/** Why the current rectangle cannot be recorded, in the operator's language. */
const Problems = ({ candidate }: { candidate: CalibrationCandidate | null }) => {
    const { t } = useTranslation();

    if (candidate === null || candidate.safe) {
        return null;
    }

    const actionable = candidate.problems.filter(problem => problem !== 'empty');

    if (actionable.length === 0) {
        return null;
    }

    return (
        <Alert
            type="error"
            showIcon
            data-testid="calibration-unsafe"
            message={t('documentScan.calibration.unsafe')}
            description={
                <ul style={{ margin: 0, paddingInlineStart: 20 }}>
                    {actionable.map(problem => (
                        <li key={problem} data-testid={`calibration-problem-${problem}`}>
                            {t(`documentScan.calibration.problem.${problem}`, {
                                bands: candidate.overlapsUnselected.join(', '),
                            })}
                        </li>
                    ))}
                </ul>
            }
        />
    );
};

/**
 * What this source's measurement looked like, as bounded numbers.
 *
 * Shown so the comparison between the two illuminations is visible to the operator and recordable
 * by the developer watching the run. It ranks nothing and selects nothing.
 */
const Quality = ({ calibration }: { calibration: CaptureCalibration }) => {
    const { t } = useTranslation();
    const diagnostics = calibration.diagnostics;

    if (!diagnostics) {
        return null;
    }

    return (
        <Descriptions
            size="small"
            column={2}
            bordered
            data-testid="calibration-quality"
            title={t('documentScan.calibration.quality')}
            items={[
                {
                    key: 'separable',
                    label: t('documentScan.calibration.separable'),
                    children: String(diagnostics.separable),
                },
                {
                    key: 'bands',
                    label: t('documentScan.calibration.acceptedBands'),
                    children: `${diagnostics.acceptedBandCount} / ${diagnostics.rawBandCount}`,
                },
                {
                    key: 'ink',
                    label: t('documentScan.calibration.inkFraction'),
                    children: diagnostics.inkFraction.toFixed(3),
                },
                {
                    key: 'rows',
                    label: t('documentScan.calibration.inkedRows'),
                    children: diagnostics.inkedRowRatio.toFixed(3),
                },
                {
                    key: 'bounds',
                    label: t('documentScan.calibration.bounds'),
                    children: t(
                        `documentScan.calibration.boundsStatus.${calibration.documentBoundsStatus}`,
                    ),
                },
                {
                    key: 'mrz',
                    label: t('documentScan.calibration.zone'),
                    children: t(
                        calibration.mrzAvailable
                            ? 'documentScan.calibration.zoneFound'
                            : 'documentScan.calibration.zoneUnknown',
                    ),
                },
            ]}
        />
    );
};
